import React, { useState, useEffect, useRef } from 'react';
import { FaSun, FaCloudRain, FaSnowflake, FaWind, FaCloud, FaSmog } from 'react-icons/fa';
import { CurrentWeather, WeatherCondition } from '@/types/enhancedTrail';
import { weatherService } from '@/services/WeatherService';
import { useLanguage } from '@/context/LanguageContext';

interface WeatherWidgetProps {
  weather: CurrentWeather;
}

const WeatherWidget: React.FC<WeatherWidgetProps> = ({ weather }) => {
  const { translate } = useLanguage();
  const [isExpanded, setIsExpanded] = useState(false);
  const [forecast, setForecast] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [currentForecastIndex, setCurrentForecastIndex] = useState(0);
  const [forecastFetched, setForecastFetched] = useState(false);
  const [showDragHint, setShowDragHint] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState<{ top?: string; left?: string }>({});
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [currentTime, setCurrentTime] = useState<string>(
    new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  );

  // Reference to the widget element
  const widgetRef = useRef<HTMLDivElement>(null);

  // Load saved position from localStorage on mount
  useEffect(() => {
    try {
      const savedPosition = localStorage.getItem('weatherWidgetPosition');
      if (savedPosition) {
        const { top, left } = JSON.parse(savedPosition);
        setPosition({ top, left });
      }
    } catch (error) {
      console.error('Failed to load widget position from localStorage:', error);
    }
  }, []);

  // Update time every minute
  useEffect(() => {
    // Update time immediately
    setCurrentTime(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));

    // Set interval to update time every minute
    const interval = setInterval(() => {
      setCurrentTime(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
    }, 60000); // Update every minute

    return () => clearInterval(interval);
  }, []);

  // Effect to rotate through forecast information
  useEffect(() => {
    if (!weather) return;

    const interval = setInterval(() => {
      setCurrentForecastIndex(prevIndex => {
        const snippets = getForecastSnippets();
        return (prevIndex + 1) % snippets.length;
      });
    }, 5000); // Change every 5 seconds

    return () => clearInterval(interval);
  }, [weather]);

  // Function to fetch forecast data when the widget is expanded
  const fetchForecastData = async () => {
    // Only fetch forecast if it hasn't been fetched yet
    if (forecastFetched || !weather) return;

    try {
      console.log('Fetching forecast data on widget expansion');
      setLoading(true);
      // For now just simulate forecast data
      // In a real implementation, you'd call weatherService.getForecast()
      setTimeout(() => {
        const mockForecast = {
          today: [
            { time: '12:00', temp: weather.temperature, icon: getWeatherIcon(weather.condition), condition: weather.condition },
            { time: '15:00', temp: weather.temperature + 1, icon: getWeatherIcon(weather.condition), condition: weather.condition },
            { time: '18:00', temp: weather.temperature - 2, icon: getWeatherIcon(weather.condition), condition: weather.condition },
          ],
          daily: [
            { day: 'Mon', minTemp: weather.temperature - 3, maxTemp: weather.temperature + 2, icon: getWeatherIcon(weather.condition) },
            { day: 'Tue', minTemp: weather.temperature - 2, maxTemp: weather.temperature + 3, icon: getWeatherIcon(weather.condition) },
            { day: 'Wed', minTemp: weather.temperature - 1, maxTemp: weather.temperature + 1, icon: getWeatherIcon(weather.condition) },
          ]
        };
        setForecast(mockForecast);
        setForecastFetched(true);
        setLoading(false);
      }, 1000);
    } catch (err) {
      console.error('Error fetching forecast data:', err);
      setLoading(false);
    }
  };

  // Generate forecast text snippets for the small view
  const getForecastSnippets = (): string[] => {
    if (!weather) return [];

    // Current conditions only (for minimized widget)
    return [
      // Current time and temperature
      `${currentTime} • ${weather.temperature}°C - ${weather.condition}`,

      // Feels like & humidity
      `Humidity: ${weather.humidity}% • Wind: ${weather.windSpeed} km/h`,
    ];
  };

  // Toggle the expanded state
  const toggleExpand = () => {
    const newExpanded = !isExpanded;
    setIsExpanded(newExpanded);

    // Fetch forecast data if expanding and forecast not yet fetched
    if (newExpanded && !forecastFetched) {
      fetchForecastData();
    }
  };

  // Handle drag start
  const handleDragStart = (e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
    setIsDragging(true);

    // Hide the drag hint as soon as user starts dragging
    setShowDragHint(false);

    if ('touches' in e) {
      // Touch event
      setDragStart({
        x: e.touches[0].clientX,
        y: e.touches[0].clientY
      });
    } else {
      // Mouse event
      setDragStart({
        x: e.clientX,
        y: e.clientY
      });
    }

    // Add listeners for drag and end events
    if ('touches' in e) {
      document.addEventListener('touchmove', handleDragMove as any, { passive: false });
      document.addEventListener('touchend', handleDragEnd as any);
    } else {
      document.addEventListener('mousemove', handleDragMove as any);
      document.addEventListener('mouseup', handleDragEnd as any);
    }

    // Prevent default to avoid text selection during drag
    e.preventDefault();
    e.stopPropagation(); // Stop event from bubbling up
  };

  // Handle drag movement
  const handleDragMove = (e: MouseEvent | TouchEvent) => {
    if (!isDragging) return;

    // Prevent all default behavior during drag
    e.preventDefault();
    e.stopPropagation();

    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;

    const deltaX = clientX - dragStart.x;
    const deltaY = clientY - dragStart.y;

    // Calculate new position based on viewport size
    const widgetElement = widgetRef.current;
    if (widgetElement) {
      const rect = widgetElement.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      // Get header and footer elements to ensure widget doesn't overlap them
      const headerHeight = document.querySelector('header')?.getBoundingClientRect().height || 0;
      const footerElement = document.querySelector('footer');
      const footerHeight = footerElement ? footerElement.getBoundingClientRect().height : 0;
      const footerTop = footerElement ? footerElement.getBoundingClientRect().top : viewportHeight;

      // Calculate boundaries to prevent overlap with header and footer
      const minTop = headerHeight + 10; // Keep 10px space from header
      const maxTop = footerTop - rect.height - 10; // Keep 10px space from footer

      // Calculate new position ensuring widget stays within viewport and doesn't overlap header/footer
      const newTop = Math.max(minTop, Math.min(maxTop, rect.top + deltaY));
      const newLeft = Math.max(0, Math.min(viewportWidth - rect.width, rect.left + deltaX));

      // Calculate the new position including limits for mobile devices
      const isMobile = window.innerWidth < 769;

      // Modal safe zone - typically modals have close buttons in the top-right corner
      // Avoid positioning the widget too close to the top-right corner
      const modalSafeZoneRight = viewportWidth * 0.25; // Top 25% of screen width from right edge
      const modalSafeZoneTop = viewportHeight * 0.2; // Top 20% of screen height

      // For mobile devices, ensure the widget is always visible by clamping positions
      if (isMobile) {
        // Ensure at least 50px of the widget is always visible on screen
        const visibleWidthLimit = Math.min(50, rect.width * 0.3);
        const visibleHeightLimit = Math.min(50, rect.height * 0.3);

        // Keep widget away from the top-right corner (modal close area)
        let safeLeft = Math.max(visibleWidthLimit - rect.width, Math.min(viewportWidth - visibleWidthLimit, newLeft));
        let safeTop = Math.max(visibleHeightLimit, Math.min(maxTop, newTop));

        // If widget is in the modal danger zone (top-right), adjust position
        if (safeTop < modalSafeZoneTop && (viewportWidth - safeLeft - rect.width) < modalSafeZoneRight) {
          if (safeTop < modalSafeZoneTop / 2) {
            // If very close to top, move it down below the modal zone
            safeTop = modalSafeZoneTop + 10;
          } else {
            // Otherwise, move it more to the left
            safeLeft = viewportWidth - modalSafeZoneRight - rect.width - 10;
          }
        }

        setPosition({
          top: `${safeTop}px`,
          left: `${safeLeft}px`
        });
      } else {
        // For desktop, use the calculated positions but still avoid modal areas
        let safeLeft = newLeft;
        let safeTop = newTop;

        // If widget is in the modal danger zone (top-right), adjust position
        if (safeTop < modalSafeZoneTop && (viewportWidth - safeLeft - rect.width) < modalSafeZoneRight) {
          if (safeTop < modalSafeZoneTop / 2) {
            // If very close to top, move it down below the modal zone
            safeTop = modalSafeZoneTop + 10;
          } else {
            // Otherwise, move it more to the left
            safeLeft = viewportWidth - modalSafeZoneRight - rect.width - 10;
          }
        }

        setPosition({
          top: `${safeTop}px`,
          left: `${safeLeft}px`
        });
      }

      // Update drag start for continuous movement
      setDragStart({
        x: clientX,
        y: clientY
      });
    }
  };

  // Handle drag end
  const handleDragEnd = () => {
    setIsDragging(false);

    // Save position to localStorage
    try {
      if (position.top && position.left) {
        localStorage.setItem('weatherWidgetPosition', JSON.stringify(position));
      }
    } catch (error) {
      console.error('Failed to save widget position to localStorage:', error);
    }

    // Remove event listeners
    document.removeEventListener('mousemove', handleDragMove as any);
    document.removeEventListener('mouseup', handleDragEnd as any);
    document.removeEventListener('touchmove', handleDragMove as any);
    document.removeEventListener('touchend', handleDragEnd as any);
  };

  // Render weather icon based on condition
  const renderWeatherIcon = (condition: WeatherCondition) => {
    switch (condition) {
      case 'sunny': return <FaSun className="text-yellow-500" />;
      case 'cloudy': return <FaCloud className="text-gray-400" />;
      case 'rainy': return <FaCloudRain className="text-blue-400" />;
      case 'snowy': return <FaSnowflake className="text-blue-200" />;
      case 'foggy': return <FaSmog className="text-gray-300" />;
      case 'windy': return <FaWind className="text-blue-300" />;
      case 'light_rain': return <FaCloudRain className="text-blue-300" />;
      case 'clear': return <FaSun className="text-yellow-400" />;
      default: return <FaSun className="text-yellow-500" />;
    }
  };

  // Helper function to get weather icon as string for forecast
  const getWeatherIcon = (condition: WeatherCondition) => {
    return condition;
  };

  // Base widget class for the container - handles fixed position and draggable effects
  const widgetClasses = `
    fixed z-50 bg-white bg-opacity-95 rounded-lg shadow-md overflow-hidden
    ${isExpanded ? 'w-80 md:w-96' : 'w-auto'} 
    transition-all duration-300 ease-in-out
    ${isDragging ? 'cursor-grabbing shadow-lg' : 'cursor-grab shadow-md'}
  `;

  // Handle position styling
  const positionStyle = {
    ...(position.top ? { top: position.top } : { top: '4rem' }),
    ...(position.left ? { left: position.left } : { right: '1rem' }),
  };

  // For initial compact view, we want to match the existing weather indicator
  if (!isExpanded) {
    return (
      <div 
        ref={widgetRef}
        className={`${widgetClasses} ${position.top ? '' : 'absolute top-4 right-4'} z-10 bg-white bg-opacity-90 p-3 rounded-lg shadow-md`}
        style={position.top ? positionStyle : {}}
        data-weather-widget="true"
      >
        {/* Drag handle */}
        <div 
          className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-blue-100 via-blue-300 to-blue-100 opacity-40 hover:opacity-60 cursor-grab"
          onMouseDown={handleDragStart}
          onTouchStart={handleDragStart}
          title="Drag to reposition weather widget"
        />

        {/* Drag hint that appears briefly */}
        {showDragHint && (
          <div className="absolute top-6 right-[-36px] bg-blue-600 bg-opacity-90 text-white py-1 px-3 rounded-md text-xs font-bold transform rotate-20 z-10 animate-fade-out pointer-events-none shadow-md">
            ← Drag Me!
          </div>
        )}

        {/* Main weather display that matches current widget */}
        <div 
          className="flex items-center"
          onClick={() => !isDragging && toggleExpand()}
        >
          <div className="text-2xl mr-2">
            {renderWeatherIcon(weather.condition)}
          </div>
          <div>
            <div className="font-semibold">{weather.temperature}°C</div>
            <div className="text-xs text-gray-600">
              {weather.condition} • {weather.windSpeed} km/h
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Expanded widget view
  return (
    <div 
      ref={widgetRef}
      className={widgetClasses}
      style={positionStyle}
      data-weather-widget="true"
    >
      {/* Drag handle */}
      <div 
        className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-blue-100 via-blue-300 to-blue-100 opacity-40 hover:opacity-60 cursor-grab"
        onMouseDown={handleDragStart}
        onTouchStart={handleDragStart}
        title="Drag to reposition weather widget"
      />

      {/* Interactive forecast line with icon section and text section */}
      <div 
        className="p-3 pt-4 bg-blue-50 bg-opacity-50 flex items-center cursor-pointer"
        onClick={() => !isDragging && toggleExpand()}
        onMouseDown={(e) => {
          // Make the header area draggable
          const isHeaderArea = e.currentTarget === e.target;
          if (isHeaderArea) {
            handleDragStart(e);
          }
        }}
        onTouchStart={(e) => {
          // Make the header area draggable
          const touchTarget = document.elementFromPoint(
            e.touches[0].clientX,
            e.touches[0].clientY
          );
          const isHeaderArea = e.currentTarget === touchTarget;
          if (isHeaderArea) {
            handleDragStart(e);
          }
        }}
      >
        {/* Left section for weather emoji */}
        <div className="w-8 h-6 flex items-center justify-center text-lg mr-3">
          {renderWeatherIcon(weather.condition)}
        </div>

        {/* Right section for scrolling text */}
        <div className="flex-1 relative h-6 overflow-visible">
          {getForecastSnippets().map((snippet, index) => (
            <div
              key={index}
              className={`absolute top-1/2 left-0 right-0 transform -translate-y-1/2 transition-opacity duration-800 whitespace-nowrap overflow-hidden text-ellipsis text-left text-sm text-blue-600 font-medium ${
                index === currentForecastIndex ? 'opacity-100' : 'opacity-0'
              }`}
            >
              {snippet}
            </div>
          ))}
        </div>
      </div>

      {/* Expandable forecast section */}
      <div className={`overflow-hidden transition-all duration-500 ease-in-out ${isExpanded ? 'max-h-96' : 'max-h-0'}`}>
        {loading ? (
          <div className="flex justify-center items-center py-8">
            <div className="w-6 h-6 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
          </div>
        ) : forecast ? (
          <>
            {/* Today's hourly forecast */}
            <div>
              <div className="px-4 py-2 text-xs font-semibold text-gray-600 bg-gray-50">
                {translate('weather.todaysForecast', "Today's Forecast")}
              </div>
              <div className="flex overflow-x-auto p-3 gap-4 scrollbar-thin">
                {forecast.today.map((item: any, index: number) => (
                  <div key={index} className="flex flex-col items-center min-w-[50px]">
                    <div className="text-xs text-gray-600 mb-1">{item.time}</div>
                    <div className="w-8 h-8 flex items-center justify-center text-lg">
                      {renderWeatherIcon(item.condition)}
                    </div>
                    <div className="text-xs font-semibold text-gray-800 mt-1">
                      {item.temp}°C
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Next days forecast */}
            <div className="border-t border-gray-100">
              <div className="px-4 py-2 text-xs font-semibold text-gray-600 bg-gray-50">
                {translate('weather.nextDays', "Next Days")}
              </div>
              <div className="p-3">
                {forecast.daily.map((day: any, index: number) => (
                  <div key={index} className="flex items-center justify-between mb-1 last:mb-0">
                    <div className="w-2/5 text-xs font-semibold text-gray-700">{day.day}</div>
                    <div className="w-2/5 flex items-center gap-1 text-xs">
                      <span className="font-semibold text-gray-800">{day.maxTemp}°</span>
                      <span className="text-gray-500">{day.minTemp}°</span>
                    </div>
                    <div className="w-1/5 flex justify-end">
                      <div className="w-6 h-6 flex items-center justify-center text-sm">
                        {renderWeatherIcon(day.icon)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Forecast summary */}
            <div className="p-3 border-t border-gray-100 text-xs text-gray-600">
              <p className="mb-1">
                <strong>{translate('weather.today', "Today")}: </strong>
                {forecast.today.map((item: any) => (
                  `${item.time} ${item.temp}°C`
                )).join(' | ')}
              </p>
              <p>
                <strong>{translate('weather.tomorrow', "Tomorrow")}: </strong>
                {`${forecast.daily[0].minTemp}° - ${forecast.daily[0].maxTemp}°`}
              </p>
            </div>
          </>
        ) : (
          <div className="p-4 text-center text-sm text-gray-500">
            {translate('weather.noForecast', "No forecast data available")}
          </div>
        )}
      </div>

      {/* Add global styles for animations */}
      <style jsx global>{`
        @keyframes fade-out {
          0% { opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { opacity: 0; }
        }
        
        .animate-fade-out {
          animation: fade-out 5s forwards;
        }
        
        .scrollbar-thin::-webkit-scrollbar {
          height: 4px;
        }
        
        .scrollbar-thin::-webkit-scrollbar-track {
          background: rgba(0, 0, 0, 0.05);
          border-radius: 4px;
        }
        
        .scrollbar-thin::-webkit-scrollbar-thumb {
          background: rgba(0, 0, 0, 0.2);
          border-radius: 4px;
        }
      `}</style>
    </div>
  );
};

export default WeatherWidget;